import type { Connector } from "../types/ocpi";
import { connectorKw, formatKw, isDc, powerTier, standardLabel, powerTypeLabel } from "../utils/format";
import { useI18n } from "../i18n/LanguageContext";
import { BRAND } from "../config/brand";
import { StatusPill } from "./StatusPill";

const TIER_BG = {
  AC: "linear-gradient(135deg, #EAF5EE 0%, #D0E9D7 100%)",
  DC_FAST: "linear-gradient(135deg, #E8F2F8 0%, #C8E0EF 100%)",
  DC_ULTRA: "linear-gradient(135deg, #EEE8F8 0%, #D9C8EF 100%)",
};

const TIER_COLOR = {
  AC: BRAND.ac,
  DC_FAST: BRAND.dcFast,
  DC_ULTRA: BRAND.dcUltra,
} as const;

export function ConnectorBadge({ connector }: { connector: Connector }) {
  const { lang } = useI18n();
  const kw = connectorKw(connector);
  const tier = powerTier(kw, isDc(connector));
  const color = TIER_COLOR[tier];
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-ink-100 p-3 transition hover:border-ink-200 hover:shadow-soft"
      style={{ background: TIER_BG[tier] }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ConnectorIcon tier={tier} color={color} />
            <span className="text-sm font-semibold text-ink-900 truncate">
              {standardLabel(connector.standard)}
            </span>
          </div>
          <div className="mt-0.5 text-[11px] text-ink-500">
            {powerTypeLabel(connector.power_type, lang)} · {connector.max_voltage}V · {connector.max_amperage}A
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[15px] font-bold tabular-nums" style={{ color }}>
            {formatKw(kw)}
          </div>
          {connector.status && (
            <div className="mt-1">
              <StatusPill status={connector.status} size="xs" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectorIcon({ tier, color }: { tier: "AC" | "DC_FAST" | "DC_ULTRA"; color: string }) {
  if (tier === "AC") {
    // Type 2 / AC: circle with notches
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.8" />
        <circle cx="12" cy="7.5" r="1.1" fill={color} />
        <circle cx="8" cy="10" r="1.1" fill={color} />
        <circle cx="16" cy="10" r="1.1" fill={color} />
        <circle cx="9.5" cy="14.5" r="1.1" fill={color} />
        <circle cx="14.5" cy="14.5" r="1.1" fill={color} />
        <path d="M12 12L12 17" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  // DC / CCS: lightning bolt
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M13 2L4 14h7l-1 8 9-12h-7l1-8z"
        fill={color}
        stroke={color}
        strokeWidth="0.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}
